// Voucher Service for Frankenfloss Booking System
// Handles voucher purchase, validation, extension and admin operations

import { refreshAccessToken } from '../config/api.config';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5174/api';

// ===== Types =====

export interface PurchaseVoucherRequest {
  amount: number;
  buyerName: string;
  buyerEmail: string;
  buyerPhone?: string;
  recipientName?: string;
  recipientEmail?: string;
  personalMessage?: string;
  giftSenderName?: string; // T-162 (v1.8.46): Zeile "Geschenkt von" auf dem Gutschein
  paymentMethod: 'paypal' | 'banktransfer' | 'stripe' | 'klarna';
  // T-006: Fahrtbezug (optional, beide zusammen), Betrag wird serverseitig geprüft
  tourTypeId?: number;
  personCount?: number;
}

export interface PurchaseVoucherResponse {
  voucherId: number;
  voucherCode: string;
  amount: number;
  paymentStatus: string;
  paymentMethod: string;
  bankTransferReference?: string;
  bankTransferInstructions?: string;
  // T-006
  tourReferenceText?: string | null;
}

export interface ValidateVoucherResponse {
  exists: boolean;
  isValid: boolean;
  message: string;
  code?: string;
  remainingAmount?: number;
  originalAmount?: number;
  validUntil?: string;
  daysUntilExpiration?: number;
  status?: string;
  // T-006: Fahrtbezug (informativ), T-035: Restwert verfällt
  tourReferenceText?: string | null;
  noPartialRedemption?: boolean;
}

/**
 * T-006: Tour-Typ, der als Gutschein angeboten wird ("Floßfahrt verschenken")
 */
export interface VoucherTourOffer {
  tourTypeId: number;
  code: string;
  name: string;
  description?: string | null;
  pricePerPerson: number;
  sortOrder: number;
}

export interface PayPalOrderResponse {
  voucherId: number;
  amount: number;
  currency: string;
  payPalOrderId?: string;
  approvalUrl?: string;
  status: string;
  message: string;
}

// T-120 (v1.8.33): Antwort beim Anlegen der Stripe-Checkout-Session
export interface StripeVoucherSessionResponse {
  voucherId: number;
  sessionId: string;
  checkoutUrl: string;
  amount: number;
}

export interface PayPalCaptureResponse {
  success: boolean;
  voucherId: number;
  voucherCode: string;
  amount: number;
  status: string;
  message: string;
}

export interface VoucherListItem {
  id: number;
  code: string;
  originalAmount: number;
  remainingAmount: number;
  status: string;
  paymentStatus: string;
  buyerName: string;
  buyerEmail: string;
  recipientName?: string;
  recipientEmail?: string;
  validUntil?: string;
  createdAt: string;
  issuedAt?: string;
  redemptions?: VoucherRedemption[];
  // T-006: Fahrtbezug (Liste liefert nur tourReferenceText, Detail alle Felder)
  tourReferenceText?: string | null;
  tourTypeId?: number | null;
  tourTypeName?: string | null;
  personCount?: number | null;
  pricePerPerson?: number | null;
  // T-150 (v1.8.44): PayPal-Prüfung am Gutschein vermerkt (Warnung im Storno-Dialog)
  payPalInPruefung?: boolean;
  // T-198 (v1.8.70): Nummer der Gutscheinrechnung (GS-JJJJ-NNNN)
  invoiceNumber?: string | null;
}

export interface VoucherRedemption {
  id: number;
  bookingId: number;
  amountRedeemed: number;
  remainingAfter: number;
  redeemedAt: string;
  notes?: string;
}

// T-043 (11.09.2026): Ergebnis von "Zahlung bestaetigen" inklusive Mail-Versandstand
export interface ConfirmPaymentResult {
  voucher: VoucherListItem;
  emailSent: boolean;
  emailRecipient: string;
  emailError?: string | null;
  message: string;
}

export interface VoucherStatistics {
  totalVouchers: number;
  activeVouchers: number;
  partiallyRedeemedVouchers: number;
  fullyRedeemedVouchers: number;
  expiredVouchers: number;
  cancelledVouchers: number;
  pendingPaymentVouchers: number;
  totalValueSold: number;
  totalValueRedeemed: number;
  totalOutstandingValue: number;
  totalExpiredValue: number;
  vouchersExpiringSoon: number;
  valueExpiringSoon: number;
  vouchersSoldThisMonth: number;
  valueSoldThisMonth: number;
  redemptionsThisMonth: number;
  valueRedeemedThisMonth: number;
}

export interface VoucherFilterParams {
  // T-198 (v1.8.70): Freitextsuche serverseitig (Code, Gutscheinrechnungsnummer, Name, E-Mail)
  suche?: string;
  code?: string;
  buyerEmail?: string;
  statuses?: string[];
  paymentStatus?: string;
  isExpired?: boolean;
  expiringWithinDays?: number;
  minAmount?: number;
  maxAmount?: number;
  createdAfter?: string;
  createdBefore?: string;
  page?: number;
  pageSize?: number;
  sortBy?: string;
  sortDescending?: boolean;
}

export interface AdminCreateVoucherRequest {
  amount: number;
  buyerName: string;
  buyerEmail: string;
  buyerPhone?: string;
  recipientName?: string;
  recipientEmail?: string;
  personalMessage?: string;
  giftSenderName?: string; // T-162 (v1.8.46): Zeile "Geschenkt von" auf dem Gutschein
  skipPayment?: boolean;
  // T-006: Fahrtbezug (optional, beide zusammen), Betrag wird serverseitig ermittelt
  tourTypeId?: number;
  personCount?: number;
}

// ===== Helper Functions =====

function getAuthToken(): string | null {
  return localStorage.getItem('authToken');
}

async function apiRequest<T>(
  method: string,
  endpoint: string,
  data?: any
): Promise<T> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };

  const token = getAuthToken();
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  let response = await fetch(`${API_BASE_URL}${endpoint}`, {
    method,
    headers,
    body: data ? JSON.stringify(data) : undefined,
  });

  // T-009 (28.08.2026): 401-Refresh-Retry NUR fuer authentifizierte Aufrufe.
  // Der Service wird auch anonym genutzt (Gutschein-Kauf durch Gaeste), deshalb
  // kein authFetch (das wuerde ohne Token werfen), sondern gezielter Retry.
  if (response.status === 401 && token) {
    const refreshed = await refreshAccessToken();
    if (refreshed) {
      headers.Authorization = `Bearer ${localStorage.getItem('authToken')}`;
      response = await fetch(`${API_BASE_URL}${endpoint}`, {
        method,
        headers,
        body: data ? JSON.stringify(data) : undefined,
      });
    }
  }

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    // T-040 (07.09.2026): Validierungsfehler des Backends (ValidationProblemDetails) lesbar machen,
    // vorher stand nur "HTTP 400" im Dialog.
    let validationText = '';
    if (errorData && typeof errorData.errors === 'object' && errorData.errors) {
      validationText = Object.values(errorData.errors as Record<string, string[]>)
        .flat()
        .filter(Boolean)
        .join(' ');
    }
    throw new Error(errorData.message || validationText || errorData.title || `HTTP ${response.status} ${response.statusText}`);
  }

  return response.json();
}

// ===== Customer API =====

export const voucherService = {
  /**
   * Purchase a new voucher (customer)
   */
  async purchaseVoucher(request: PurchaseVoucherRequest): Promise<PurchaseVoucherResponse> {
    return apiRequest<PurchaseVoucherResponse>('POST', '/vouchers/purchase', request);
  },

  /**
   * Validate a voucher code (public)
   */
  async validateVoucher(code: string): Promise<ValidateVoucherResponse> {
    return apiRequest<ValidateVoucherResponse>('GET', `/vouchers/validate/${encodeURIComponent(code)}`);
  },

  /**
   * T-006: Tour-Typen, die als Gutschein angeboten werden (anonym, Kaufseite und Admin-Dialog)
   */
  async getTourOffers(): Promise<VoucherTourOffer[]> {
    return apiRequest<VoucherTourOffer[]>('GET', '/vouchers/tour-offers');
  },

  // ===== PayPal Integration =====

  /**
   * Create PayPal order for voucher purchase
   */
  async createPayPalOrder(
    voucherId: number,
    returnUrl?: string,
    cancelUrl?: string,
    code?: string
  ): Promise<PayPalOrderResponse> {
    // T-026-Rest (02.09.2026): Gutschein-Code aus der Kaufantwort als Besitznachweis (Backend prueft)
    return apiRequest<PayPalOrderResponse>('POST', '/vouchers/paypal/create-order', {
      voucherId,
      returnUrl,
      cancelUrl,
      code,
    });
  },

  /**
   * Capture PayPal order after customer approval
   */
  async capturePayPalOrder(
    voucherId: number,
    payPalOrderId: string
  ): Promise<PayPalCaptureResponse> {
    return apiRequest<PayPalCaptureResponse>('POST', '/vouchers/paypal/capture-order', {
      voucherId,
      payPalOrderId,
    });
  },

  /**
   * T-120 (v1.8.33): Stripe-Checkout-Session für den Gutscheinkauf mit Kreditkarte anlegen (Code als Besitznachweis)
   */
  async createStripeSession(
    voucherId: number,
    code: string,
    successUrl: string,
    cancelUrl: string
  ): Promise<StripeVoucherSessionResponse> {
    return apiRequest<StripeVoucherSessionResponse>('POST', '/vouchers/stripe/create-session', {
      voucherId,
      code,
      successUrl,
      cancelUrl,
    });
  },

  /**
   * T-120 (v1.8.33): Rückkehr von Stripe, Server prüft die Session und aktiviert den Gutschein
   */
  async confirmStripePayment(voucherId: number, sessionId: string): Promise<PayPalCaptureResponse> {
    return apiRequest<PayPalCaptureResponse>('POST', '/vouchers/stripe/confirm', { voucherId, sessionId });
  },
};

// ===== Admin API =====

export const adminVoucherService = {
  /**
   * Get list of vouchers with filtering (Admin)
   */
  async getVouchers(params?: VoucherFilterParams): Promise<{ items: VoucherListItem[]; totalCount: number }> {
    const queryParams = new URLSearchParams();

    if (params) {
      if (params.suche && params.suche.trim()) queryParams.append('suche', params.suche.trim());
      if (params.code) queryParams.append('code', params.code);
      if (params.buyerEmail) queryParams.append('buyerEmail', params.buyerEmail);
      if (params.statuses?.length) queryParams.append('statuses', params.statuses.join(','));
      if (params.paymentStatus) queryParams.append('paymentStatus', params.paymentStatus);
      if (params.isExpired !== undefined) queryParams.append('isExpired', String(params.isExpired));
      if (params.expiringWithinDays) queryParams.append('expiringWithinDays', String(params.expiringWithinDays));
      if (params.minAmount) queryParams.append('minAmount', String(params.minAmount));
      if (params.maxAmount) queryParams.append('maxAmount', String(params.maxAmount));
      if (params.createdAfter) queryParams.append('createdAfter', params.createdAfter);
      if (params.createdBefore) queryParams.append('createdBefore', params.createdBefore);
      if (params.page) queryParams.append('page', String(params.page));
      if (params.pageSize) queryParams.append('pageSize', String(params.pageSize));
      if (params.sortBy) queryParams.append('sortBy', params.sortBy);
      if (params.sortDescending !== undefined) queryParams.append('sortDirection', params.sortDescending ? 'desc' : 'asc');
    }

    const queryString = queryParams.toString();
    const endpoint = queryString ? `/admin/vouchers?${queryString}` : '/admin/vouchers';

    return apiRequest<{ items: VoucherListItem[]; totalCount: number }>('GET', endpoint);
  },

  /**
   * Get voucher by ID (Admin)
   */
  async getVoucher(id: number): Promise<VoucherListItem> {
    return apiRequest<VoucherListItem>('GET', `/admin/vouchers/${id}`);
  },

  /**
   * Create voucher manually (Admin)
   */
  async createVoucher(request: AdminCreateVoucherRequest): Promise<VoucherListItem> {
    return apiRequest<VoucherListItem>('POST', '/admin/vouchers', request);
  },

  /**
   * Cancel a voucher (Admin)
   */
  async cancelVoucher(id: number, reason: string): Promise<VoucherListItem> {
    return apiRequest<VoucherListItem>('PUT', `/admin/vouchers/${id}/cancel`, { reason });
  },

  /**
   * Confirm bank transfer payment (Admin)
   */
  // T-043 (11.09.2026): Backend liefert jetzt { voucher, emailSent, emailRecipient, emailError, message },
  // damit der Admin sieht, ob die Gutschein-Mail nach der Zahlungsbestaetigung wirklich raus ist.
  async confirmPayment(id: number, paymentReference: string): Promise<ConfirmPaymentResult> {
    return apiRequest<ConfirmPaymentResult>('PUT', `/admin/vouchers/${id}/confirm-payment`, { paymentReference });
  },

  /**
   * Get voucher statistics (Admin)
   */
  async getStatistics(): Promise<VoucherStatistics> {
    return apiRequest<VoucherStatistics>('GET', '/admin/vouchers/statistics');
  },
};

// ===== Predefined Amounts =====

export const VOUCHER_AMOUNTS = [50, 100, 150, 200, 250, 300, 350, 400, 450, 500];

// T-059 (v1.8.33, R5-Test Erik 28.09.2026): Mindestbetrag 50 € wie das Backend (PurchaseVoucherDto) und der kleinste Festbetrag
export const MIN_CUSTOM_AMOUNT = 50;
export const MAX_CUSTOM_AMOUNT = 1000;

// ===== Status Helpers =====

export const VOUCHER_STATUS_LABELS: Record<string, string> = {
  PendingPayment: 'Zahlung ausstehend',
  Active: 'Aktiv',
  PartiallyRedeemed: 'Teilweise eingelöst',
  FullyRedeemed: 'Vollständig eingelöst',
  Expired: 'Abgelaufen',
  Cancelled: 'Storniert',
  Transferred: 'Übertragen',
};

export const VOUCHER_STATUS_COLORS: Record<string, string> = {
  PendingPayment: 'orange',
  Active: 'green',
  PartiallyRedeemed: 'blue',
  FullyRedeemed: 'gray',
  Expired: 'red',
  Cancelled: 'red',
  Transferred: 'purple',
};

export const PAYMENT_STATUS_LABELS: Record<string, string> = {
  Pending: 'Ausstehend',
  Paid: 'Bezahlt',
  Failed: 'Fehlgeschlagen',
  Refunded: 'Erstattet',
};

export function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('de-DE', {
    style: 'currency',
    currency: 'EUR',
  }).format(amount);
}

export function formatDate(dateString: string): string {
  return new Date(dateString).toLocaleDateString('de-DE', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
}

export function formatDateTime(dateString: string): string {
  return new Date(dateString).toLocaleString('de-DE', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export default voucherService;
